import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import Feedback from "../Views/Operations/Feedback";

const isTokenValid = (expiresOn: number | undefined) => {
  if (!expiresOn) return false;
  const currentTime = Math.floor(Date.now() / 1000);
  return expiresOn > currentTime;
};

const ProtectedRoute: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const refreshToken = async () => {
    try {
      if (accounts.length > 0) {
        const tokenResponse = await instance.acquireTokenSilent({
          // scopes: ["User.Read"],
          scopes: [`${process.env.REACT_APP_CLIENT_ID}/.default` , "offline_access"],
          account: accounts[0],
        });

        localStorage.setItem("tokenKey", tokenResponse.accessToken);
        if (tokenResponse.expiresOn) {
          localStorage.setItem("tokenExpiresOn", String(tokenResponse.expiresOn.getTime() / 1000));
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    }
  };

  useEffect(() => {
    const checkAuthentication = async () => {
      const storedToken = localStorage.getItem("tokenKey");
      const storedExpiresOn = localStorage.getItem("tokenExpiresOn");

      // If token exists and is valid
      if (storedToken && storedExpiresOn && isTokenValid(Number(storedExpiresOn))) {
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      // If token exists but expired, try to refresh
      if (storedToken) {
        const refreshSuccess = await refreshToken();
        setIsAuthenticated(refreshSuccess);
        setLoading(false);
        return;
      }

      // No token but has MSAL account
      if (accounts.length > 0) {
        const refreshSuccess = await refreshToken();
        setIsAuthenticated(refreshSuccess);
      } else {
        setIsAuthenticated(false);
      }

      setLoading(false);
    };

    checkAuthentication();
  }, [instance, accounts, location]);


  // Add this to your ProtectedRoute component
useEffect(() => {
  if (!isAuthenticated) return;
  
  const storedExpiresOn = localStorage.getItem("tokenExpiresOn");
  if (!storedExpiresOn) return;
  
  const expiresOn = Number(storedExpiresOn);
  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = expiresOn - currentTime;
  
  // Refresh token 5 minutes before expiry
  if (timeUntilExpiry > 300) {
    const refreshTimer = setTimeout(async () => {
      await refreshToken();
    }, (timeUntilExpiry - 300) * 1000);
    
    return () => clearTimeout(refreshTimer);
  } else {
    // If less than 5 minutes left, refresh immediately
    refreshToken();
  }
}, [isAuthenticated]);

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return isAuthenticated ? <><Outlet /><Feedback /></> : <Navigate to="/login" replace state={{ from: location }} />;
};

export default ProtectedRoute;